import { useEffect, useState } from "react";
import {
  collection,
  documentId,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "./config";
import { ensureFirebaseAuthReady } from "./auth-ready";

interface FirebaseDocState<T> {
  data: T | null;
  /** True once the first `onSnapshot` callback has fired (doc present OR absent). */
  hasSnapshot: boolean;
}

/**
 * Subscribe to a single Firestore document by id and return its data live.
 *
 * Unlike `useFirebaseData`, this does **not** pre-check the document's
 * existence: a missing doc yields `data = null` and the listener **stays
 * attached**, so when the doc is later created the update arrives live. That
 * suits docs the backend creates lazily — e.g. the per-user notification signal
 * doc, written only on the first notification. There is nothing to "validate"
 * ahead of time: the doc id is derived from the caller's own user id, so a user
 * only ever subscribes to their own document.
 *
 * `hasSnapshot` lets callers distinguish "no snapshot yet" from "snapshot said
 * the doc is absent" so they can baseline correctly and not fire on mount.
 *
 * It listens through a QUERY (`accessibleUserIds array-contains <uid>` plus the
 * doc id), not a single-doc listener, because of the Firestore rules: they read
 * `resource.data`, so a single-doc listen on a not-yet-created doc is DENIED
 * and the listener dies — the doc's later creation would never arrive. A query
 * filtered on the user's own uid is allowed whether or not the doc exists, and
 * delivers it the moment the backend creates it.
 */
export function useFirebaseDoc<T = Record<string, unknown>>(
  collectionName?: string,
  docId?: string,
  enabled = true,
): FirebaseDocState<T> {
  const [state, setState] = useState<FirebaseDocState<T>>({
    data: null,
    hasSnapshot: false,
  });

  useEffect(() => {
    if (!enabled || !collectionName || !docId) {
      setState({ data: null, hasSnapshot: false });
      return;
    }

    // Reset on (re)subscribe so a stale value/baseline from a prior target
    // (e.g. a different user) doesn't leak into the new subscription.
    setState({ data: null, hasSnapshot: false });

    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    void (async () => {
      // Sign in to Firebase as this user first — the rules scope reads to the uid.
      const firebaseUid = await ensureFirebaseAuthReady();
      if (cancelled || !firebaseUid) return;

      unsubscribe = onSnapshot(
        query(
          collection(db, collectionName),
          where("accessibleUserIds", "array-contains", firebaseUid),
          where(documentId(), "==", docId),
        ),
        (snapshot) => {
          const match = snapshot.docs[0];
          setState({
            data: match ? (match.data() as T) : null,
            hasSnapshot: true,
          });
        },
        (error) => {
          // Non-fatal: keep the last state; the query's natural refetch still
          // reconciles. Logged for diagnostics.
          console.error("[firebase] useFirebaseDoc listener error:", error);
        },
      );
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [collectionName, docId, enabled]);

  return state;
}
